// Session 147j+2 — OI Flow tab (Phase-1 ODX-equivalent on free OI data).
//
// Three panels:
//   1. Live ATM ± N strike grid — call / strike / put with ΔOI lots and
//      quadrant-coloured cells (green long-build, red short-build, etc.).
//   2. Per-minute bias timeline — last 6h of NEUTRAL/BULL/BEAR signals
//      with arrow counts and conviction.
//   3. Confluence audit — % of LQP/DHOL signals that AGREED with OI bias.
//
// Threshold tuner inline on top so the operator can raise/lower the
// "≥3 arrows same side" rule per symbol without redeploying.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Activity, TrendingUp, TrendingDown, Minus, RefreshCw, Settings,
  ArrowUpRight, ArrowDownRight, AlertCircle, Target, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import TriggerReplayChart from './TriggerReplayChart';
import PredictabilityAudit from './PredictabilityAudit';
import OIVwapPanel from './OIVwapPanel';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token';

const SYMBOLS = ['NIFTY', 'BANKNIFTY'];

// Quadrant cell colour palette.
const QUADRANT_TONE = {
  LONG_BUILD:  'bg-emerald-500/20 border-emerald-500/60 text-emerald-200',
  SHORT_BUILD: 'bg-rose-500/20 border-rose-500/60 text-rose-200',
  SHORT_COVER: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300/80',
  LONG_UNWIND: 'bg-rose-500/10 border-rose-500/30 text-rose-300/80',
};

const QUADRANT_LABEL = {
  LONG_BUILD:  'Long build',
  SHORT_BUILD: 'Short build',
  SHORT_COVER: 'Short cover',
  LONG_UNWIND: 'Long unwind',
};

const BIAS_TONE = {
  BULL: 'bg-emerald-500/20 border-emerald-500/60 text-emerald-200',
  BEAR: 'bg-rose-500/20 border-rose-500/60 text-rose-200',
  NEUTRAL: 'bg-surface-highlight border-border text-muted-foreground',
};

const fmtLots = (n) => (n == null ? '—' : (n > 0 ? '+' : '') + Number(n).toFixed(0));
const fmtOI = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (v >= 1e7) return (v / 1e7).toFixed(2) + ' Cr';
  if (v >= 1e5) return (v / 1e5).toFixed(2) + ' L';
  if (v >= 1e3) return (v / 1e3).toFixed(1) + ' K';
  return String(v);
};

const authHeaders = () => {
  const tok = localStorage.getItem(TOKEN_KEY);
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

// ---------------------------------------------------------------------
// Status bar
// ---------------------------------------------------------------------
const StatusBar = ({ status, onSnapshot, onRunNow, busy }) => {
  if (!status) {
    return (
      <div className="text-xs text-muted-foreground" data-testid="oi-flow-status-loading">
        Loading status…
      </div>
    );
  }
  const last = status.last_snapshot;
  const lastSig = status.last_signal;
  return (
    <div
      className="flex items-center gap-3 flex-wrap"
      data-testid="oi-flow-status-bar"
    >
      <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-mono uppercase tracking-wider border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
        <Activity className="w-3 h-3" />
        Live · {status.now_ist_minute || '--:--'}
      </span>
      {last ? (
        <span className="text-[11px] font-mono text-muted-foreground">
          last snap: <span className="text-foreground">{last.symbol}</span> @ {last.ist_minute?.slice(11)}
        </span>
      ) : (
        <span className="text-[11px] font-mono text-amber-300">
          no snapshot yet
        </span>
      )}
      {lastSig ? (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${BIAS_TONE[lastSig.bias] || BIAS_TONE.NEUTRAL}`}>
          {lastSig.bias === 'BULL' ? <TrendingUp className="w-3 h-3" />
            : lastSig.bias === 'BEAR' ? <TrendingDown className="w-3 h-3" />
              : <Minus className="w-3 h-3" />}
          {lastSig.symbol} · {lastSig.bias} · {lastSig.bull_arrows}↑ / {lastSig.bear_arrows}↓
        </span>
      ) : null}
      <div className="ml-auto flex items-center gap-2">
        <button
          onClick={onSnapshot}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono uppercase tracking-wider border border-border hover:border-accent hover:text-accent disabled:opacity-50"
          data-testid="oi-flow-snapshot-btn"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
          Snapshot now
        </button>
        <button
          onClick={onRunNow}
          disabled={busy}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono uppercase tracking-wider border border-border hover:border-accent hover:text-accent disabled:opacity-50"
          data-testid="oi-flow-run-now-btn"
        >
          <Zap className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
          Compute bias
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Threshold tuner card
// ---------------------------------------------------------------------
const ConfigCard = ({ symbol, config, onSave }) => {
  const [edit, setEdit] = useState(null);
  useEffect(() => { setEdit(config); }, [config]);
  if (!edit) return null;
  const dirty = JSON.stringify(edit) !== JSON.stringify(config);
  const num = (k) => (e) => setEdit({ ...edit, [k]: Number(e.target.value) });
  return (
    <div className="border border-border bg-surface p-3" data-testid="oi-flow-config-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] font-mono uppercase tracking-wider text-foreground flex items-center gap-1.5">
          <Settings className="w-3 h-3" /> Tuning · {symbol}
        </div>
        <label className="text-[10px] font-mono uppercase tracking-wider flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={!!edit.enabled}
            onChange={(e) => setEdit({ ...edit, enabled: e.target.checked })}
            data-testid={`oi-flow-config-enabled-${symbol}`}
          />
          {edit.enabled ? 'Enabled' : 'Disabled'}
        </label>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[10px] font-mono">
        {[
          { key: 'lot_size', label: 'Lot size' },
          { key: 'min_oi_delta_lots', label: 'Min ΔOI (lots)' },
          { key: 'min_ltp_delta', label: 'Min |ΔLTP|' },
          { key: 'conviction_arrows', label: 'Conviction arrows' },
          { key: 'strike_window', label: 'Strike window ±N' },
          { key: 'min_notional_cr', label: 'Notional ₹Cr / strike' },
          { key: 'notional_trigger_strikes', label: 'Notional · min strikes' },
        ].map((f) => (
          <div key={f.key} className="flex flex-col">
            <span className="text-muted-foreground uppercase tracking-wider mb-0.5">{f.label}</span>
            <input
              type="number"
              value={edit[f.key] ?? 0}
              step={f.key === 'min_ltp_delta' ? 0.01 : f.key === 'min_notional_cr' ? 0.5 : 1}
              onChange={num(f.key)}
              className="bg-surface-highlight border border-border px-2 py-1 text-foreground"
              data-testid={`oi-flow-config-${f.key}-${symbol}`}
            />
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-end gap-2">
        {dirty && (
          <span className="text-[10px] font-mono text-amber-300 mr-auto">Unsaved changes</span>
        )}
        <button
          onClick={() => setEdit(config)}
          disabled={!dirty}
          className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-border hover:border-accent disabled:opacity-30"
          data-testid={`oi-flow-config-reset-${symbol}`}
        >Reset</button>
        <button
          onClick={() => onSave(edit)}
          disabled={!dirty}
          className="px-3 py-1 text-[10px] font-mono uppercase tracking-wider border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-30"
          data-testid={`oi-flow-config-save-${symbol}`}
        >Save</button>
      </div>
    </div>
  );
};

// Session 147j+14 — Directional Notional Flow trigger banner
const NotionalFlowBanner = ({ grid }) => {
  if (!grid) return null;
  const bias = grid.notional_bias || 'NEUTRAL';
  const bull = grid.notional_bull_strikes || 0;
  const bear = grid.notional_bear_strikes || 0;
  const thresh = grid.notional_threshold_cr;
  const minStrikes = grid.notional_strikes_threshold;
  const tone =
    bias === 'BEAR'
      ? 'border-rose-500/60 bg-rose-500/10 text-rose-200'
      : bias === 'BULL'
      ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-200'
      : 'border-border bg-surface-highlight text-muted-foreground';
  const Icon = bias === 'BEAR' ? TrendingDown : bias === 'BULL' ? TrendingUp : Minus;
  const label =
    bias === 'BEAR'
      ? 'BEARISH NOTIONAL FLOW'
      : bias === 'BULL'
      ? 'BULLISH NOTIONAL FLOW'
      : 'NO TRIGGER';
  return (
    <div
      className={`px-3 py-2 border ${tone} flex items-center gap-3 flex-wrap text-[11px] font-mono uppercase tracking-wider`}
      data-testid={`oi-flow-notional-banner-${grid.symbol}`}
      title={`≥${minStrikes} strikes within ATM window where BOTH legs cleared ₹${thresh} Cr AND quadrants align (call-write+put-buy → BEAR · call-buy+put-write → BULL).`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="font-bold">{grid.symbol} · {label}</span>
      <span className="text-foreground/80">
        bull match {bull} · bear match {bear}
      </span>
      <span className="ml-auto text-muted-foreground">
        ≥{minStrikes} strikes · ₹{thresh} Cr/leg
      </span>
    </div>
  );
};

const fmtCr = (n) => {
  if (n == null) return '—';
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return '—';
  return v.toFixed(2) + ' Cr';
};
const StrikeGrid = ({ symbol, grid }) => {
  if (!grid) return null;
  if (!grid.strikes || grid.strikes.length === 0) {
    return (
      <div
        className="border border-border bg-surface p-4 text-center text-xs text-muted-foreground"
        data-testid={`oi-flow-grid-empty-${symbol}`}
      >
        No OI snapshots persisted yet for {symbol}. The 1-min loop will
        start populating once the WS option-tick feed is live (market hours).
      </div>
    );
  }
  return (
    <div className="border border-border bg-surface" data-testid={`oi-flow-grid-${symbol}`}>
      <div className="px-3 py-2 border-b border-border flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider">
        <Target className="w-3 h-3 text-accent" />
        {symbol} · ATM grid · minute <span className="text-foreground">{grid.ist_minute?.slice(11)}</span>
        <span className="ml-auto text-muted-foreground">
          {grid.strikes.length} strikes
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead>
            <tr className="border-b border-border bg-surface-highlight text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-2 py-1.5 text-right">CE OI</th>
              <th className="px-2 py-1.5 text-right">CE ΔLots</th>
              <th className="px-2 py-1.5 text-right" title="|ΔOI × LTP| in ₹ Cr — flow notional for the call leg">CE ₹ Flow</th>
              <th className="px-2 py-1.5 text-center">CE Quadrant</th>
              <th className="px-2 py-1.5 text-center">Strike</th>
              <th className="px-2 py-1.5 text-center">PE Quadrant</th>
              <th className="px-2 py-1.5 text-right" title="|ΔOI × LTP| in ₹ Cr — flow notional for the put leg">PE ₹ Flow</th>
              <th className="px-2 py-1.5 text-right">PE ΔLots</th>
              <th className="px-2 py-1.5 text-right">PE OI</th>
            </tr>
          </thead>
          <tbody>
            {grid.strikes.map((row) => {
              const matchTone =
                row.notional_match === 'BEAR'
                  ? 'bg-rose-500/10'
                  : row.notional_match === 'BULL'
                  ? 'bg-emerald-500/10'
                  : '';
              const thresh = grid.notional_threshold_cr || 0;
              const ceHot = (row.ce_notional_cr || 0) >= thresh;
              const peHot = (row.pe_notional_cr || 0) >= thresh;
              return (
              <tr
                key={row.strike}
                className={`border-b border-border/40 ${matchTone}`}
                data-testid={`oi-flow-row-${symbol}-${row.strike}`}
                data-notional-match={row.notional_match || ''}
              >
                <td className="px-2 py-1 text-right text-emerald-300/80">{fmtOI(row.ce_oi)}</td>
                <td className={`px-2 py-1 text-right ${(row.ce_delta_oi_lots || 0) > 0 ? 'text-emerald-300' : (row.ce_delta_oi_lots || 0) < 0 ? 'text-rose-300' : 'text-muted-foreground'}`}>
                  {fmtLots(row.ce_delta_oi_lots)}
                </td>
                <td className={`px-2 py-1 text-right font-semibold ${ceHot ? (row.ce_quadrant === 'SHORT_BUILD' ? 'text-rose-300' : row.ce_quadrant === 'LONG_BUILD' ? 'text-emerald-300' : 'text-foreground') : 'text-muted-foreground/60'}`}>
                  {fmtCr(row.ce_notional_cr)}
                </td>
                <td className="px-1 py-1 text-center">
                  {row.ce_quadrant ? (
                    <span
                      className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider border ${QUADRANT_TONE[row.ce_quadrant]}`}
                      data-testid={`oi-flow-cell-${symbol}-${row.strike}-CE`}
                    >
                      {QUADRANT_LABEL[row.ce_quadrant]}
                    </span>
                  ) : (
                    <span className="text-muted-foreground/50">—</span>
                  )}
                </td>
                <td className="px-2 py-1 text-center font-bold text-foreground">{row.strike}</td>
                <td className="px-1 py-1 text-center">
                  {row.pe_quadrant ? (
                    <span
                      className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider border ${QUADRANT_TONE[row.pe_quadrant]}`}
                      data-testid={`oi-flow-cell-${symbol}-${row.strike}-PE`}
                    >
                      {QUADRANT_LABEL[row.pe_quadrant]}
                    </span>
                  ) : (
                    <span className="text-muted-foreground/50">—</span>
                  )}
                </td>
                <td className={`px-2 py-1 text-right font-semibold ${peHot ? (row.pe_quadrant === 'LONG_BUILD' ? 'text-rose-300' : row.pe_quadrant === 'SHORT_BUILD' ? 'text-emerald-300' : 'text-foreground') : 'text-muted-foreground/60'}`}>
                  {fmtCr(row.pe_notional_cr)}
                </td>
                <td className={`px-2 py-1 text-right ${(row.pe_delta_oi_lots || 0) > 0 ? 'text-emerald-300' : (row.pe_delta_oi_lots || 0) < 0 ? 'text-rose-300' : 'text-muted-foreground'}`}>
                  {fmtLots(row.pe_delta_oi_lots)}
                </td>
                <td className="px-2 py-1 text-right text-rose-300/80">{fmtOI(row.pe_oi)}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Per-minute bias timeline
// ---------------------------------------------------------------------
const BiasTimeline = ({ symbol, rows }) => (
  <div className="border border-border bg-surface" data-testid={`oi-flow-timeline-${symbol}`}>
    <div className="px-3 py-2 border-b border-border text-[11px] font-mono uppercase tracking-wider">
      {symbol} · Bias timeline · last {rows?.length || 0} minutes
    </div>
    {(!rows || rows.length === 0) ? (
      <div className="p-4 text-center text-xs text-muted-foreground" data-testid={`oi-flow-timeline-empty-${symbol}`}>
        No bias signals yet — needs ≥2 consecutive snapshots.
      </div>
    ) : (
      <div className="max-h-[280px] overflow-y-auto">
        <table className="w-full text-[11px] font-mono">
          <thead className="sticky top-0 bg-surface-highlight">
            <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-2 py-1.5 text-left">Minute</th>
              <th className="px-2 py-1.5 text-center">Bias</th>
              <th className="px-2 py-1.5 text-right">↑ Bull</th>
              <th className="px-2 py-1.5 text-right">↓ Bear</th>
              <th className="px-2 py-1.5 text-right">Conviction</th>
              <th className="px-2 py-1.5 text-left">SR anchors</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.ist_minute} className="border-b border-border/40 hover:bg-surface-highlight">
                <td className="px-2 py-1 text-foreground">{r.ist_minute?.slice(11)}</td>
                <td className="px-2 py-1 text-center">
                  <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wider border ${BIAS_TONE[r.bias] || BIAS_TONE.NEUTRAL}`}>
                    {r.bias}
                  </span>
                </td>
                <td className="px-2 py-1 text-right text-emerald-300">{r.bull_arrows}</td>
                <td className="px-2 py-1 text-right text-rose-300">{r.bear_arrows}</td>
                <td className="px-2 py-1 text-right text-foreground">{r.conviction?.toFixed(1)}</td>
                <td className="px-2 py-1 text-left text-[10px] text-muted-foreground">
                  {(r.sr_anchors || []).slice(0, 3).map((a, i) => (
                    <span
                      key={i}
                      className={`inline-block mr-1 px-1 py-0.5 border text-[9px] uppercase tracking-wider ${a.type === 'RESISTANCE' ? 'border-rose-500/40 text-rose-300' : a.type === 'SUPPORT' ? 'border-emerald-500/40 text-emerald-300' : 'border-amber-500/40 text-amber-300'}`}
                    >
                      {a.type[0]}@{a.strike}
                    </span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------
// Confluence audit
// ---------------------------------------------------------------------
const ConfluenceAuditCard = ({ stats }) => {
  if (!stats) return null;
  const buckets = stats.stats || {};
  const sources = Object.keys(buckets);
  return (
    <div className="border border-border bg-surface" data-testid="oi-flow-confluence-audit">
      <div className="px-3 py-2 border-b border-border text-[11px] font-mono uppercase tracking-wider flex items-center gap-2">
        <ArrowUpRight className="w-3 h-3 text-emerald-300" />
        <ArrowDownRight className="w-3 h-3 text-rose-300" />
        Confluence audit · last {stats.days || 7}d · {stats.sample_count || 0} signals
      </div>
      {sources.length === 0 ? (
        <div className="p-4 text-center text-xs text-muted-foreground">
          No emitted signals carry an OI confluence stamp yet. Will populate
          as LQP / DHOL / SLINGSHOT_EP fire during market hours.
        </div>
      ) : (
        <table className="w-full text-[11px] font-mono">
          <thead className="bg-surface-highlight">
            <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-2 py-1.5 text-left">Pattern</th>
              <th className="px-2 py-1.5 text-right">Total</th>
              <th className="px-2 py-1.5 text-right text-emerald-300">Agree</th>
              <th className="px-2 py-1.5 text-right text-rose-300">Disagree</th>
              <th className="px-2 py-1.5 text-right text-muted-foreground">Neutral</th>
              <th className="px-2 py-1.5 text-right">Agree %</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((src) => {
              const b = buckets[src];
              const total = b.total || 0;
              const pct = total ? ((b.AGREE / total) * 100).toFixed(0) : '0';
              return (
                <tr key={src} className="border-b border-border/40">
                  <td className="px-2 py-1 text-foreground">{src}</td>
                  <td className="px-2 py-1 text-right">{total}</td>
                  <td className="px-2 py-1 text-right text-emerald-300">{b.AGREE || 0}</td>
                  <td className="px-2 py-1 text-right text-rose-300">{b.DISAGREE || 0}</td>
                  <td className="px-2 py-1 text-right text-muted-foreground">{b.NEUTRAL || 0}</td>
                  <td className="px-2 py-1 text-right text-foreground">{pct}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------
const OIFlowTab = () => {
  const [activeSym, setActiveSym] = useState('NIFTY');
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [grid, setGrid] = useState(null);
  const [recent, setRecent] = useState(null);
  const [confluence, setConfluence] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [s, c, g, r, cf] = await Promise.all([
        axios.get(`${API}/api/admin/oi-flow/status`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/oi-flow/config?symbol=${activeSym}`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/oi-flow/grid?symbol=${activeSym}`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/oi-flow/recent?symbol=${activeSym}&hours=6`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/oi-flow/confluence-stats?days=7`, { headers: authHeaders() }),
      ]);
      setStatus(s.data);
      setConfig(c.data);
      setGrid(g.data);
      setRecent(r.data?.rows || []);
      setConfluence(cf.data);
    } catch (e) {
      // First load may fail if user not yet admin-authed.
      console.warn('[OIFlow] loadAll failed:', e?.response?.status || e?.message);
    }
  }, [activeSym]);

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 30 * 1000); // refresh every 30s
    return () => clearInterval(t);
  }, [loadAll]);

  const onSnapshot = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/oi-flow/snapshot-now`, {}, { headers: authHeaders() });
      toast.success('Snapshot persisted');
      await loadAll();
    } catch (e) {
      toast.error(`Snapshot failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onRunNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/oi-flow/run-now?symbol=${activeSym}`,
        {},
        { headers: authHeaders() },
      );
      toast.success(`Bias=${r.data.bias} · ${r.data.bull_arrows}↑ / ${r.data.bear_arrows}↓`);
      await loadAll();
    } catch (e) {
      toast.error(`Compute failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const onSaveConfig = async (next) => {
    setBusy(true);
    try {
      const r = await axios.put(
        `${API}/api/admin/oi-flow/config?symbol=${activeSym}`,
        next,
        { headers: authHeaders() },
      );
      setConfig(r.data);
      toast.success(`${activeSym} tuning saved`);
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 p-4" data-testid="oi-flow-tab">
      {/* Header */}
      <div>
        <h2 className="font-heading text-2xl font-bold text-foreground tracking-tight">
          OI Flow
        </h2>
        <p className="text-xs text-muted-foreground font-mono mt-1">
          ODX-equivalent bias from per-minute Δ(OI × Price) on the option chain.
          ≥3 same-side arrows in one minute → directional conviction.
        </p>
      </div>

      {/* Symbol switch */}
      <div className="flex items-center gap-1 border border-border bg-surface p-1 w-fit" data-testid="oi-flow-sym-tabs">
        {SYMBOLS.map((s) => (
          <button
            key={s}
            onClick={() => setActiveSym(s)}
            className={`px-3 py-1 text-[11px] font-mono uppercase tracking-wider transition-colors ${activeSym === s ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`oi-flow-sym-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Status */}
      <div className="border border-border bg-surface p-3">
        <StatusBar
          status={status}
          onSnapshot={onSnapshot}
          onRunNow={onRunNow}
          busy={busy}
        />
      </div>

      {/* Config tuner */}
      <ConfigCard symbol={activeSym} config={config} onSave={onSaveConfig} />

      {/* Live grid */}
      <NotionalFlowBanner grid={grid} />
      <StrikeGrid symbol={activeSym} grid={grid} />

      {/* Bias timeline */}
      <BiasTimeline symbol={activeSym} rows={recent} />

      {/* Session 147j+14 — Trigger Replay (5m candles + BUY/SELL markers) */}
      <TriggerReplayChart symbol={activeSym} />

      {/* Session 147j+15 — Predictability audit (did OI/volume predict
          today's Nifty futures move?) */}
      <PredictabilityAudit symbol={activeSym} />

      {/* Session 147j+16 — OI-VWAP confluence panel (futures OHLC+OI from Dhan) */}
      <div className="border border-border bg-surface p-3" data-testid="oi-vwap-section">
        <OIVwapPanel symbol={activeSym} />
      </div>

      {/* Confluence audit */}
      <ConfluenceAuditCard stats={confluence} />

      {/* Educational footer — what the colours mean */}
      <div className="border border-border bg-surface p-3 text-[10px] font-mono text-muted-foreground" data-testid="oi-flow-legend">
        <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-foreground">
          <AlertCircle className="w-3 h-3" /> Reading the cells
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          <div><span className={`inline-block px-1 py-0.5 text-[9px] uppercase tracking-wider border ${QUADRANT_TONE.LONG_BUILD}`}>Long build</span> ΔOI↑ ΔPrice↑ — buyers winning. CE → bullish · PE → bearish.</div>
          <div><span className={`inline-block px-1 py-0.5 text-[9px] uppercase tracking-wider border ${QUADRANT_TONE.SHORT_BUILD}`}>Short build</span> ΔOI↑ ΔPrice↓ — writers winning. CE → bearish (cap) · PE → bullish (floor).</div>
          <div><span className={`inline-block px-1 py-0.5 text-[9px] uppercase tracking-wider border ${QUADRANT_TONE.SHORT_COVER}`}>Short cover</span> ΔOI↓ ΔPrice↑ — writers exiting (weak signal).</div>
          <div><span className={`inline-block px-1 py-0.5 text-[9px] uppercase tracking-wider border ${QUADRANT_TONE.LONG_UNWIND}`}>Long unwind</span> ΔOI↓ ΔPrice↓ — longs exiting (weak signal).</div>
        </div>
        <div className="mt-3 pt-2 border-t border-border/40 text-[10px]">
          <div className="text-[11px] uppercase tracking-wider text-foreground mb-1">Notional Flow Trigger</div>
          ₹ Flow per leg = |ΔOI × LTP| / 1 Cr.
          A strike <strong>matches</strong> when both legs clear the threshold (default ₹8 Cr) AND quadrants align.
          <span className="block mt-1">
            <span className="text-rose-300">BEARISH</span> = call-write (CE Short build) + put-buy (PE Long build).
            <span className="text-emerald-300 ml-3">BULLISH</span> = call-buy (CE Long build) + put-write (PE Short build).
            Trigger fires when ≥3 strikes match within the ATM window.
          </span>
        </div>
      </div>
    </div>
  );
};

export default OIFlowTab;
